<template>
  <div class="info-form">
    <b-form-group
      ref="trustlyName"
      :label="$t('field.name.label')"
      :label-for="'name-on-trustly-' + _uid"
      label-cols-md="3"
      class="required"
    >
      <!-- See the same field in the various <Type>Info components -->
      <b-form-input
        :id="'name-on-trustly-' + _uid"
        v-model="trustlyData.userName"
        :state="!v$.trustlyData.userName.$error"
        name="user-name"
        :aria-describedby="'name-on-trustly-validation-errors-' + _uid"
        @blur="validateNameAndEmit"
      />
      <ValidationErrors
        :id="'name-on-trustly-validation-errors-' + _uid"
        :validator="v$.trustlyData.userName"
        :errors="{
          required: $t('field.name.required'),
          maxLength: $t('field_max_length', { len: 100 }),
          notOnlyDigits: $t('validation.cannot_contain_only_digits'),
        }"
      />
    </b-form-group>

    <BillingAddressInfo
      ref="billingAddressInfo"
      :billing-data="trustlyData.billingAddress"
      :disabled-fields="disabledFields"
      :default-billing-address="defaultBillingAddress"
      :pre-fill-address-label="preFillAddressLabel"
    />

    <PaymentFormFooter
      ref="paymentFormFooter"
      :payment-data="trustlyData"
      :nickname-placeholder="$t('bank.nickname.placeholder')"
      :show-save-checkbox="showSaveCheckbox"
      :only-anonymous-tenders="onlyAnonymousTenders"
      :is-selection-view="isSelectionView"
      :show-one-time-use-disclosure="false"
      tender-type="bank"
      @cancel="$emit('cancel')"
      @save="$emit('save')"
    />
  </div>
</template>

<script>
import BillingAddressInfo from '@grantstreet/psc-vue/components/BillingAddressInfo.vue'
import { useVuelidate } from '@vuelidate/core'
import { required, maxLength } from '@vuelidate/validators'
import ValidationErrors from '@grantstreet/psc-vue/components/ValidationErrors.vue'
import EventBus from '@grantstreet/psc-vue/utils/event-bus.ts'
import PaymentFormFooter from './PaymentFormFooter.vue'

export default {
  emits: ['save', 'cancel'],
  components: {
    BillingAddressInfo,
    ValidationErrors,
    PaymentFormFooter,
  },

  setup () {
    return {
      v$: useVuelidate(),
    }
  },

  props: {
    trustlyData: {
      type: Object,
      required: true,
    },
    disabledFields: {
      type: Object,
      required: true,
    },
    defaultBillingAddress: {
      type: Object,
      default: () => ({}),
    },
    preFillAddressLabel: {
      type: Object,
      required: false,
      default: null,
    },
    showSaveCheckbox: {
      type: Boolean,
      default: false,
    },
    isSelectionView: {
      type: Boolean,
      required: true,
    },
  },

  data () {
    return {}
  },

  computed: {},

  validations: {
    trustlyData: {
      userName: {
        required,
        maxLength: maxLength(100),
        notOnlyDigits (value) {
          if (!value) {
            return true
          }
          return !/^\d+$/.test(value.trim())
        },
      },
    },
  },

  mounted () {
    // Return current state unless asked for a re-validation
    EventBus.$on('ewallet.trustlyValidation', this.handleValidation)
    EventBus.$on('ewallet.validation.billingData', this.emitValidation)
  },

  beforeUnmount () {
    EventBus.$off('ewallet.trustlyValidation', this.handleValidation)
    EventBus.$off('ewallet.validation.billingData', this.emitValidation)
  },

  methods: {
    // Two different ways to handle validation. By parent request
    // (this.$ref.validate()) and upon request via event (see mounted hook).

    // When a parent asks for validation directly check both local input and
    // children
    validate () {
      this.v$.$touch()
      return !this.v$.$invalid && this.$refs.billingAddressInfo.validate()
    },

    // Each component with fields responds to these events individually so the
    // $refs aren't touched
    handleValidation ({ touch }) {
      return touch ? this.validateLocalAndEmit() : this.emitValidation()
    },

    emitValidation () {
      EventBus.$emit('ewallet.validation.trustlyData', !this.v$.$invalid && this.$refs.billingAddressInfo.validate())
    },

    validateLocalAndEmit () {
      this.v$.trustlyData.userName.$touch()
      this.$refs.billingAddressInfo.v$.$touch()
      this.emitValidation()
    },

    validateNameAndEmit () {
      this.v$.trustlyData.userName.$touch()
      this.emitValidation()
    },

    initialize () {
      this.v$.$reset()
      this.$refs.billingAddressInfo.v$.$reset()
    },

    focusOnError () {
      if (this.v$.trustlyData.userName.$error) {
        this.$refs.trustlyName.focus()
      }
      else if (this.$refs.billingAddressInfo.v$.$errors.length) {
        this.$refs.billingAddressInfo.focusOnError()
      }
    },
  },
}
</script>
